import { ConfirmDialog } from '@/components/ConfirmDialog';
import { NewProjectDialog } from '@/components/NewProjectDialog';
import { Avatar } from '@/components/ui';
import { useChannels, useDeleteProject, useProject, useProjects } from '@/hooks/queries';
import { daysRemaining, formatDateRange, readableOn } from '@/lib/design';
import { useAuth } from '@/stores/auth';
import { useProjectStore } from '@/stores/project';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

/**
 * Armazon de la aplicacion: barra lateral indigo oscura con el selector de
 * proyecto, la navegacion y el sprint en curso; barra superior con el titulo de
 * la vista, la busqueda y la accion principal.
 */
export function AppLayout() {
  const user = useAuth((state) => state.user);
  const logout = useAuth((state) => state.logout);
  const navigate = useNavigate();
  const location = useLocation();

  const { data: projects } = useProjects();
  const { activeProjectId, setActiveProject } = useProjectStore();
  const { data: project } = useProject(activeProjectId);
  const { data: channels } = useChannels();

  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const [confirmingProjectDelete, setConfirmingProjectDelete] = useState(false);
  const deleteProject = useDeleteProject();

  /**
   * Concilia el proyecto guardado en el navegador con los que existen de
   * verdad. Si se quedo apuntando a uno borrado (o al de otra cuenta) hay que
   * soltarlo: si no, todas las vistas piden datos de un proyecto inexistente y
   * la pantalla se llena de errores.
   */
  useEffect(() => {
    if (!projects) {
      return; // Todavia cargando: no se decide nada.
    }
    if (projects.length === 0) {
      if (activeProjectId) {
        setActiveProject(null);
      }
      return;
    }
    const stillExists = projects.some((p) => p.id === activeProjectId);
    if (!stillExists) {
      setActiveProject(projects[0].id);
    }
  }, [projects, activeProjectId, setActiveProject]);

  const activeProject = projects?.find((p) => p.id === activeProjectId) ?? null;
  const activeSprint = project?.sprints.find((s) => s.status === 'ACTIVE') ?? null;
  const unread = channels?.reduce((sum, c) => sum + c.unreadCount, 0) ?? 0;

  const sprintProgress = (() => {
    if (!activeSprint?.stats || activeSprint.stats.items === 0) {
      return 0;
    }
    return Math.round((activeSprint.stats.done / activeSprint.stats.items) * 100);
  })();

  const { title, subtitle } = viewMeta(location.pathname, {
    sprintName: activeSprint?.name,
    projectName: activeProject?.name,
    sprintRange: activeSprint ? formatDateRange(activeSprint.startDate, activeSprint.endDate) : '',
    itemCount: project?.stats.items ?? 0,
    memberCount: project?.members.length ?? 0,
    channelCount: channels?.length ?? 0,
  });

  return (
    <div className="flex h-screen overflow-hidden bg-canvas text-ink">
      <aside className="flex w-sidebar flex-none flex-col bg-nav text-nav-text">
        <div className="flex items-center gap-2.5 px-[18px] pb-3.5 pt-[18px]">
          <div
            aria-hidden="true"
            className="flex size-[30px] items-center justify-center rounded-[9px] font-display text-[15px] font-bold text-white"
            style={{ background: 'linear-gradient(135deg,#14b8a6,#22d3ee)' }}
          >
            S
          </div>
          <span className="font-display text-base font-bold tracking-[-.01em]">SynQ</span>
        </div>

        <div className="relative px-3 pb-3">
          <button
            type="button"
            onClick={() => setSwitcherOpen((open) => !open)}
            aria-expanded={switcherOpen}
            aria-haspopup="listbox"
            className="flex w-full items-center gap-2.5 rounded-[9px] border border-nav-card-line bg-nav-card px-[11px] py-2.5 text-left"
          >
            <span
              aria-hidden="true"
              className="flex size-[22px] flex-none items-center justify-center rounded-md font-display text-[11px] font-semibold"
              style={{
                background: activeProject?.color ?? '#2f3257',
                color: readableOn(activeProject?.color ?? '#2f3257'),
              }}
            >
              {activeProject?.key.slice(0, 2) ?? '··'}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12.5px] font-semibold">
                {activeProject?.name ?? 'Sin proyecto'}
              </span>
              <span className="block text-[10.5px] text-nav-muted">
                {activeProject ? 'Proyecto activo' : 'Crea uno para empezar'}
              </span>
            </span>
            <span aria-hidden="true" className="text-[10px] text-nav-muted">
              ▾
            </span>
          </button>

          {switcherOpen && (
            <ul
              role="listbox"
              className="absolute inset-x-3 z-20 mt-1 overflow-hidden rounded-[9px] border border-nav-card-line bg-nav-card py-1 shadow-lg shadow-black/40"
            >
              {projects?.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={p.id === activeProjectId}
                    onClick={() => {
                      setActiveProject(p.id);
                      setSwitcherOpen(false);
                    }}
                    className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] transition-colors hover:bg-nav-hover ${
                      p.id === activeProjectId ? 'font-semibold text-white' : 'text-nav-item'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className="size-2 flex-none rounded-full"
                      style={{ background: p.color }}
                    />
                    <span className="truncate">{p.name}</span>
                  </button>
                </li>
              ))}

              <li className="mt-1 border-t border-nav-card-line pt-1">
                <button
                  type="button"
                  onClick={() => {
                    setSwitcherOpen(false);
                    setCreatingProject(true);
                  }}
                  className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] font-semibold text-nav-item transition-colors hover:bg-nav-hover hover:text-white"
                >
                  <span aria-hidden="true" className="w-2 text-center">
                    +
                  </span>
                  Nuevo proyecto
                </button>
              </li>

              {activeProject && project?.canManage && (
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      setSwitcherOpen(false);
                      setConfirmingProjectDelete(true);
                    }}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[12.5px] font-semibold text-danger-text transition-colors hover:bg-nav-hover"
                  >
                    <span aria-hidden="true" className="w-2 text-center">
                      ×
                    </span>
                    Eliminar {activeProject.name}
                  </button>
                </li>
              )}
            </ul>
          )}

          {/* Sin proyectos la barra lateral no lleva a ninguna parte: el unico
              camino posible es crear el primero. */}
          {projects?.length === 0 && (
            <button
              type="button"
              onClick={() => setCreatingProject(true)}
              className="mt-2 w-full rounded-[9px] bg-brand px-3 py-2 text-[12.5px] font-semibold text-brand-on transition-colors hover:bg-brand-strong"
            >
              + Crear el primero
            </button>
          )}
        </div>

        <nav className="flex flex-col gap-0.5 px-3 py-1.5">
          <p className="section-label px-2.5 pb-1.5 pt-2.5 text-nav-label">Trabajo</p>
          <NavItem to="/tablero" icon="▦" label="Tablero de sprint" />
          <NavItem to="/backlog" icon="☰" label="Backlog" />
          <NavItem to="/chat" icon="◍" label="Chat" badge={unread} />
          <NavItem to="/equipo" icon="◎" label="Equipo" />
          <NavItem to="/proyectos" icon="▤" label="Proyectos" />
          <NavItem to="/historial" icon="◷" label="Historial" />
          {user?.role === 'ADMIN' && <NavItem to="/admin/usuarios" icon="⚇" label="Usuarios" />}
        </nav>

        {activeSprint && (
          <div className="flex flex-col gap-0.5 px-3 py-1.5">
            <p className="section-label px-2.5 pb-1.5 pt-3.5 text-nav-label">Sprint actual</p>
            <div className="flex flex-col gap-[7px] px-2.5">
              <div className="flex justify-between text-xs">
                <span className="text-nav-item">{activeSprint.name}</span>
                <span className="text-nav-muted">{sprintProgress}%</span>
              </div>
              <div
                role="progressbar"
                aria-valuenow={sprintProgress}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`Avance de ${activeSprint.name}`}
                className="h-[5px] overflow-hidden rounded bg-nav-track"
              >
                <div
                  className="h-full rounded"
                  style={{
                    width: `${sprintProgress}%`,
                    background: 'linear-gradient(90deg,#14b8a6,#2dd4bf)',
                  }}
                />
              </div>
              <p className="text-[11px] text-nav-muted">
                {remainingLabel(activeSprint.endDate)} ·{' '}
                {formatDateRange(activeSprint.startDate, activeSprint.endDate)}
              </p>
            </div>
          </div>
        )}

        <div className="mt-auto flex items-center gap-2.5 border-t border-nav-line p-3">
          <Avatar user={user ? { id: user.id, fullName: user.fullName, avatarUrl: user.avatarUrl } : null} size={28} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12.5px] font-semibold">{user?.fullName}</p>
            <p className="truncate text-[10.5px] text-nav-muted">
              {user?.jobTitle ?? (user?.role === 'ADMIN' ? 'Administrador' : 'Miembro')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void logout().then(() => navigate('/login', { replace: true }))}
            title="Cerrar sesion"
            aria-label="Cerrar sesion"
            className="rounded-md px-1.5 py-1 text-nav-muted transition-colors hover:bg-nav-hover hover:text-white"
          >
            ⏻
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-topbar flex-none items-center gap-3.5 border-b border-line bg-surface px-5">
          <h1 className="font-display text-base font-semibold tracking-[-.01em]">{title}</h1>
          <p className="text-[12.5px] text-subtle">{subtitle}</p>
          <div className="flex-1" />

          <label className="flex h-[34px] w-[260px] items-center gap-2 rounded-control border border-line-strong bg-canvas px-[11px] text-[13px] text-faint focus-within:border-brand">
            <span aria-hidden="true">⌕</span>
            <input
              type="search"
              placeholder="Buscar tareas, personas…"
              aria-label="Buscar"
              className="min-w-0 flex-1 bg-transparent text-ink outline-none placeholder:text-faint"
            />
          </label>

          <button
            type="button"
            aria-label={unread > 0 ? `Notificaciones: ${unread} sin leer` : 'Notificaciones'}
            onClick={() => navigate('/chat')}
            className="relative flex size-[34px] items-center justify-center rounded-control border border-line-strong transition-colors hover:bg-muted-surface"
          >
            <span aria-hidden="true">🔔</span>
            {unread > 0 && (
              <span className="absolute right-[7px] top-[6px] size-[7px] rounded-full border-[1.5px] border-surface bg-alert" />
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate('/backlog?nueva=1')}
            className="flex h-[34px] items-center gap-[7px] rounded-control bg-brand px-3.5 text-[13px] font-semibold text-brand-on transition-colors hover:bg-brand-strong"
          >
            + Nueva tarea
          </button>
        </header>

        <Outlet />
      </div>

      {confirmingProjectDelete && activeProject && (
        <ConfirmDialog
          title={`Eliminar ${activeProject.name}`}
          description={
            <>
              Se borraran <strong>todas sus tareas, sprints y el canal de chat</strong> del
              proyecto. No hay forma de recuperarlo.
            </>
          }
          requireTyping={activeProject.key}
          pending={deleteProject.isPending}
          error={deleteProject.error ? (deleteProject.error as Error).message : null}
          onCancel={() => setConfirmingProjectDelete(false)}
          onConfirm={() =>
            deleteProject.mutate(activeProject.id, {
              onSuccess: () => {
                setConfirmingProjectDelete(false);
                setActiveProject(null);
              },
            })
          }
        />
      )}

      {creatingProject && (
        <NewProjectDialog
          onClose={() => setCreatingProject(false)}
          onCreated={(projectId) => {
            setCreatingProject(false);
            setActiveProject(projectId);
            navigate('/backlog');
          }}
        />
      )}
    </div>
  );
}

function NavItem({
  to,
  icon,
  label,
  badge,
}: {
  to: string;
  icon: string;
  label: string;
  badge?: number;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-2.5 rounded-control px-2.5 py-2 text-[13.5px] transition-colors ${
          isActive
            ? 'bg-nav-active font-semibold text-white'
            : 'text-nav-item hover:bg-nav-hover hover:text-white'
        }`
      }
    >
      <span aria-hidden="true" className="w-[18px] text-center text-[13px]">
        {icon}
      </span>
      <span className="flex-1">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="rounded-[10px] bg-brand px-[7px] py-px text-[10.5px] font-bold text-brand-on">
          {badge}
        </span>
      )}
    </NavLink>
  );
}

function remainingLabel(endDate: string | null): string {
  const days = daysRemaining(endDate);
  if (days === null) {
    return 'Sin fecha de cierre';
  }
  if (days === 0) {
    return 'Ultimo dia';
  }
  return `Quedan ${days} ${days === 1 ? 'dia' : 'dias'}`;
}

interface ViewContext {
  sprintName?: string;
  projectName?: string;
  sprintRange: string;
  itemCount: number;
  memberCount: number;
  channelCount: number;
}

/** Titulo y subtitulo de la barra superior segun la ruta. */
function viewMeta(pathname: string, ctx: ViewContext): { title: string; subtitle: string } {
  if (pathname.startsWith('/backlog')) {
    return {
      title: 'Backlog del producto',
      subtitle: `${ctx.itemCount} ${ctx.itemCount === 1 ? 'elemento' : 'elementos'} priorizados`,
    };
  }
  if (pathname.startsWith('/chat')) {
    return {
      title: 'Chat del equipo',
      subtitle: `${ctx.channelCount} ${ctx.channelCount === 1 ? 'canal' : 'canales'}`,
    };
  }
  if (pathname.startsWith('/historial')) {
    return { title: 'Historial de sprints', subtitle: 'Lo que se comprometio y lo que se entrego' };
  }
  if (pathname.startsWith('/proyectos')) {
    return {
      title: 'Proyectos',
      subtitle: 'Quien participa en cada uno',
    };
  }
  if (pathname.startsWith('/equipo')) {
    return {
      title: 'Equipo',
      subtitle: `${ctx.memberCount} ${ctx.memberCount === 1 ? 'persona' : 'personas'}`,
    };
  }
  if (pathname.startsWith('/admin/usuarios')) {
    return { title: 'Usuarios', subtitle: 'Altas, roles y accesos' };
  }
  return {
    title: ctx.sprintName ?? 'Tablero',
    subtitle: ctx.sprintName
      ? `${ctx.projectName ?? ''} · ${ctx.sprintRange}`
      : 'Sin sprint en curso',
  };
}
